import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {DataSetDimensionsGroupsState} from './dataSetDimensionsGroups';
import {
  createDataSetDimensionsGroupSuccess,
  deleteDataSetDimensionsGroupSuccess,
  initializeDataSetDimensionsGroups,
  initializeDataSetDimensionsGroupsSuccess,
  loadDataSetDimensionsGroups,
  loadDataSetDimensionsGroupsSuccess,
  updateDataSetDimensionsGroups,
  updateDataSetDimensionsGroupsSuccess,
  updateDataSetDimensionsGroupSuccess
} from './dataSetDimensionsGroups.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, Serializers} from '../../interfaces/serializer';
import {DataSetDimensionsGroup} from '../../interfaces/dimensions';
import {
  DatasetDimensionsGroupComponentObject,
  DatasetDimensionsGroupComponentObjects
} from '../../classes/dataset-dimensions-group-component-object';
import {DimensionComponentObjects} from '../../classes/dimension-component-object';
import {Sort} from '../../libraries/sort';

const _reducer: ActionReducer<DataSetDimensionsGroupsState> = createReducer(
  { serialized: [], componentObjects: [], loaded: false, loading: false, initialized: false },
  on(loadDataSetDimensionsGroups, (state: DataSetDimensionsGroupsState): DataSetDimensionsGroupsState => {
    return {
      ...state,
      loaded: false,
      loading: true
    };
  }),
  on(loadDataSetDimensionsGroupsSuccess, (state: DataSetDimensionsGroupsState, action: TypedAction<string> & { groups: Serializers<DataSetDimensionsGroup> }): DataSetDimensionsGroupsState => {
    return {
      ...state,
      serialized: action.groups,
      componentObjects: _sort(action.groups.map((group: Serializer<DataSetDimensionsGroup>): DatasetDimensionsGroupComponentObject => new DatasetDimensionsGroupComponentObject(group))),
      loading: false,
      loaded: true
    };
  }),
  on(updateDataSetDimensionsGroups, (state: DataSetDimensionsGroupsState): DataSetDimensionsGroupsState => {
    return {
      ...state,
      loading: true
    };
  }),
  on(updateDataSetDimensionsGroupsSuccess, (state: DataSetDimensionsGroupsState, action: TypedAction<string> & { groups: Serializers<DataSetDimensionsGroup> }): DataSetDimensionsGroupsState => {
    action.groups.forEach((dimension: Serializer<DataSetDimensionsGroup>): void => {
      const componentObject: DatasetDimensionsGroupComponentObject = state.componentObjects.find((componentObject: DatasetDimensionsGroupComponentObject): boolean => componentObject.payload.id === dimension.id);

      if (componentObject) {
        componentObject.update(dimension);
      } else {
        state.componentObjects.push(new DatasetDimensionsGroupComponentObject(dimension));
      }
    });

    state.componentObjects = state.componentObjects.filter((group: DatasetDimensionsGroupComponentObject): boolean => action.groups.includes(group.payload as Serializer<DataSetDimensionsGroup>));

    return {
      ...state,
      serialized: action.groups,
      componentObjects: _sort(state.componentObjects),
      loading: false,
      loaded: true
    };
  }),
  on(createDataSetDimensionsGroupSuccess, (state: DataSetDimensionsGroupsState, action: TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }): DataSetDimensionsGroupsState => {
    return {
      ...state,
      serialized: [
        ...state.serialized,
        action.group
      ],
      componentObjects: _sort([
        ...state.componentObjects,
        new DatasetDimensionsGroupComponentObject(action.group)
      ])
    }
  }),
  on(updateDataSetDimensionsGroupSuccess, (state: DataSetDimensionsGroupsState, action: TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }): DataSetDimensionsGroupsState => {
    state.componentObjects.find((group: DatasetDimensionsGroupComponentObject): boolean => group.payload.id === action.group.id).update(action.group);

    return {
      ...state,
      serialized: state.serialized.map((group: Serializer<DataSetDimensionsGroup>): Serializer<DataSetDimensionsGroup> => group.id === action.group.id && action.group || group),
      componentObjects: _sort(state.componentObjects)
    };
  }),
  on(deleteDataSetDimensionsGroupSuccess, (state: DataSetDimensionsGroupsState, action: TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }): DataSetDimensionsGroupsState => {
    return {
      ...state,
      serialized: state.serialized.filter((group: Serializer<DataSetDimensionsGroup>): boolean => group.id !== action.group.id),
      componentObjects: state.componentObjects.filter((group: DatasetDimensionsGroupComponentObject): boolean => group.payload.id !== action.group.id)
    }
  }),
  on(initializeDataSetDimensionsGroups, (state: DataSetDimensionsGroupsState): DataSetDimensionsGroupsState => {
    return {
      ...state,
      initialized: false
    };
  }),
  on(initializeDataSetDimensionsGroupsSuccess, (state: DataSetDimensionsGroupsState, action: TypedAction<string> & { generalDimensions: DimensionComponentObjects, datasetDimensions: DimensionComponentObjects, datasetConditionalDimensions: DimensionComponentObjects }): DataSetDimensionsGroupsState => {
    state.componentObjects.forEach((group: DatasetDimensionsGroupComponentObject): void => {
      group.init(action.generalDimensions, action.datasetDimensions, action.datasetConditionalDimensions);
    });

    return {
      ...state,
      initialized: true
    };
  })
);

export const dataSetDimensionsGroupsReducer: ActionReducer<DataSetDimensionsGroupsState> = (state: DataSetDimensionsGroupsState, action: Action) => {
  return _reducer(state, action);
};

function _sort(groups: DatasetDimensionsGroupComponentObjects): DatasetDimensionsGroupComponentObjects {
  return [
    groups.find((group: DatasetDimensionsGroupComponentObject): boolean => group.payload.attributes.slug === 'general'),
    groups.find((group: DatasetDimensionsGroupComponentObject): boolean => group.payload.attributes.slug === 'date'),
    ...groups
      .filter((group: DatasetDimensionsGroupComponentObject): boolean => !['general', 'date'].includes(group.payload.attributes.slug))
      .sort((a: DatasetDimensionsGroupComponentObject, b: DatasetDimensionsGroupComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name))
  ];
}
